import React, { useState, useEffect, useMemo } from "react";
import DialogContent from "@mui/material/DialogContent";
import { IAircraft, ISwapAircraftEventData } from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { swapAircraftEvents } from "src/redux/actions/flights/FlightData.Actions";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { noAircraftSelected } from "src/redux/reducers/FlightTimelineReducer";
import { DialogActions, useTheme } from "@mui/material";
import { ModalTitle, ModalButton, ModalErrorText } from "../DialogModalComponents"; //ModalWarningText
import { EventModalTypes } from "../DialogModalWrapper";
// form components
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { DialogModalStyles } from "../DialogModalWrapper";

interface ISwapAircraftModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
  timeInputLabel1?: string;
  timeInputLabel2?: string;
  /** Flight display (mosaic) window this modal was opened from; its aircraft selection pre-fills the swap. */
  windowId: string;
}

interface IACOption {
  value: string;
  registration: string;
  title: string;
}

/**
 *  Modal to gather user info for swapping flights to a different aircraft
 *  ISwapAircraftModalProps
 */
export const SwapAircraftModal = (props: ISwapAircraftModalProps): JSX.Element => {
  const { isOpen, handleClose, setActiveEventModal, timeInputLabel1, timeInputLabel2, windowId } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const aircraft = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);
  // Pre-fills from the selection of the flight display window this modal was opened from.
  const selectedAircraftArray =
    useSelector((state: IRootState) => state.timelineFlights.selectedAircraft?.[windowId]) ?? noAircraftSelected;

  const [timeRange, setTimeRange] = useState<{
    startTime: string;
    endTime: string;
  }>({ startTime: "", endTime: "" });
  const [oldAC, setOldAC] = useState<IACOption>(null);
  const [newAC, setNewAC] = useState<IACOption>(null);
  const [flightsOnly, setFlightsOnly] = useState<boolean>(true);

  //error checking
  const [timeError, setTimeError] = useState<string>(null);
  const [oldACError, setOldACError] = useState<string>(null);
  const [newACError, setNewACError] = useState<string>(null);

  const aircraftOptions: IACOption[] = React.useMemo(() => {
    return aircraft.map((craft: IAircraft): IACOption => {
      return {
        value: craft._id,
        registration: craft.registration,
        title: craft.aircraftType.title,
      };
    });
  }, [aircraft]);

  useEffect(() => {
    const firstAircraft = selectedAircraftArray?.[0];
    const selectedAircraft1 = aircraftOptions.find((option: IACOption) => firstAircraft === option.value);
    if (selectedAircraft1) setNewAC(selectedAircraft1);
  }, [aircraftOptions, selectedAircraftArray]);

  useEffect(() => {
    const secondAircraft = selectedAircraftArray?.[1];
    const selectedAircraft2 = aircraftOptions.find((option: IACOption) => secondAircraft === option.value);
    if (selectedAircraft2) setOldAC(selectedAircraft2);
  }, [aircraftOptions, selectedAircraftArray]);

  //set labels & error strings so they can be configurable
  const timeInputLabel: string[] = useMemo(
    () => [timeInputLabel1 ? timeInputLabel1 : "From:", timeInputLabel2 ? timeInputLabel2 : "Until:"],
    [timeInputLabel1, timeInputLabel2]
  );

  //////////////
  // HANDLERS //
  //////////////

  const handleMinDateChange = (event: any): void => {
    const time = { ...timeRange };
    time.startTime = event;
    setTimeRange(time);
  };

  const handleMaxDateChange = (event: any): void => {
    const time = { ...timeRange };
    time.endTime = event;
    setTimeRange(time);
  };

  const selectInputOrginalACHandler = (event: IACOption): void => {
    setOldAC(event);
    setOldACError(null);
  };

  const selectInputNewACHandler = (event: IACOption): void => {
    setNewAC(event);
    setNewACError(null);
  };

  const handleCancel = (): void => {
    //quit modal
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  useEffect(() => {
    if (!timeRange.startTime || !timeRange.endTime) {
      const timeErrorString = "Date Range Required";
      setTimeError(timeErrorString);
    } else if (moment(timeRange.startTime).toDate() > moment(timeRange.endTime).toDate()) {
      const timeErrorString = `${timeInputLabel[0]} cannot be before ${timeInputLabel[1]} `;
      setTimeError(timeErrorString);
    } else {
      setTimeError(null);
    }

    if (oldAC === null) {
      setOldACError("Original Aircraft Required");
    } else {
      setOldACError(null);
    }

    if (newAC === null) {
      setNewACError("New Aircraft Required");
    } else {
      setNewACError(null);
    }
  }, [newAC, oldAC, timeInputLabel, timeRange.endTime, timeRange.startTime]);

  const containsErrors: boolean = !!timeError || !!oldACError || !!newACError;

  const handleConfirm = (): void => {
    if (containsErrors) {
      return;
    }
    const data: ISwapAircraftEventData = {
      firstAircraftId: oldAC.value,
      secondAircraftId: newAC.value,
      startDate: moment(timeRange.startTime).toDate()?.valueOf(),
      endDate: moment(timeRange.endTime).toDate()?.valueOf(),
      includeGroundEvents: !flightsOnly,
    };
    dispatch(swapAircraftEvents(data));
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  if (isOpen) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content">
          <ModalTitle title="Swap Aircraft"></ModalTitle>

          {/* {!oldAC && !newAC && <ModalWarningText warning={"No Aircraft selected"}></ModalWarningText>} */}

          <SelectField
            className="modal_selection"
            name="oldAC"
            label="Original AC:"
            value={oldAC}
            onChange={selectInputOrginalACHandler}
            options={aircraftOptions}
            getOptionLabel={(AC: IACOption) => `${AC.registration} (${AC.title})`}
            getOptionValue={(AC: IACOption) => AC.registration}
            noOptionsMessage={() => "Search for airport name or IATA code"}
            isSearchable={true}
            isClearable={true}
            menuPosition="fixed"
            maxMenuHeight={400}
            openMenuOnFocus={false}
            placeholder="Original Aircraft"
          />

          {!!oldACError && <ModalErrorText error={oldACError}></ModalErrorText>}

          <SelectField
            className="modal_selection"
            name="newAC"
            label="New AC:"
            value={newAC}
            onChange={selectInputNewACHandler}
            options={aircraftOptions}
            getOptionLabel={(AC: IACOption) => `${AC.registration} (${AC.title})`}
            getOptionValue={(AC: IACOption) => AC.registration}
            noOptionsMessage={() => "Search for airport name or IATA code"}
            isSearchable={true}
            isClearable={true}
            menuPosition="fixed"
            maxMenuHeight={400}
            openMenuOnFocus={false}
            placeholder="New Aircraft"
          />

          {!!newACError && <ModalErrorText error={newACError}></ModalErrorText>}

          <DateTimeField
            // className=""
            name="aircraftSwapStartTime"
            label={timeInputLabel[0]}
            value={timeRange?.startTime}
            onChange={handleMinDateChange}
            maxDate={timeRange.endTime}
          />

          <DateTimeField
            // className=""
            name="aircraftSwapEndTime"
            label={timeInputLabel[1]}
            value={timeRange?.endTime}
            onChange={handleMaxDateChange}
            minDate={timeRange.startTime}
          />

          {!!timeError && <ModalErrorText error={timeError}></ModalErrorText>}

          <CheckboxField
            className={"checkboxModal"}
            label="Flights Only"
            checked={flightsOnly}
            onChange={() => setFlightsOnly(!flightsOnly)}
          />

          <DialogActions>
            <ModalButton label="Cancel" handler={handleCancel}></ModalButton>
            <ModalButton label="Confirm" disable={containsErrors} handler={handleConfirm}></ModalButton>
          </DialogActions>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
